import { formatComp, formatDollars } from '@/lib/useRetailStores';
import {
  deltaRatio,
  partialCaptionSuffix,
  selectedChannelRows,
  useHomeSalesTrend,
  type HomeFilters,
} from '@/lib/useHomeDashboard';
import { TileMessage, TileShell, TileSkeleton } from './TileShell';
import { TrendChart } from './TrendChart';
import { MUTED_TEXT, NOT_AVAILABLE, deltaClass } from './tileText';

export function SalesVsLastYear({ filters }: { filters: HomeFilters }) {
  const { data, isLoading, unavailable } = useHomeSalesTrend(filters);
  const delta = data ? deltaRatio(data.total_ty, data.total_ly) : null;
  const channels = data ? selectedChannelRows(data.by_channel, filters) : [];

  return (
    <TileShell
      label="Sales"
      title="Sales vs. last year"
      caption={
        data
          ? [`${data.range.label} vs. same days last year`, partialCaptionSuffix(data.range)]
              .filter(Boolean)
              .join(' ')
          : 'Compared to the same days last year'
      }
    >
      {isLoading ? (
        <TileSkeleton rows={5} />
      ) : unavailable || !data ? (
        <TileMessage>{NOT_AVAILABLE}</TileMessage>
      ) : data.points.length === 0 && data.total_ty === 0 ? (
        <TileMessage>No sales data yet.</TileMessage>
      ) : (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,320px)_1fr]">
          <div className="flex flex-col gap-4">
            <div>
              <div className={`text-[13px] ${MUTED_TEXT}`}>{data.range.label}</div>
              <div className="text-[30px] font-semibold leading-tight text-amp-ink dark:text-amp-dark-ink">
                {formatDollars(data.total_ty)}
              </div>
              <div className="text-[13px]">
                <span className={deltaClass(delta)}>{formatComp(delta)}</span>{' '}
                <span className={MUTED_TEXT}>vs {formatDollars(data.total_ly)} last year</span>
              </div>
            </div>
            {channels.length > 0 && (
              <div className="flex flex-col gap-1.5 border-t border-amp-line pt-3 dark:border-amp-dark-line">
                {channels.map((c) => {
                  const d = deltaRatio(c.ty, c.ly);
                  return (
                    <div key={c.channel} className="flex items-baseline justify-between gap-3 text-[13px]">
                      <span className={MUTED_TEXT}>{c.label}</span>
                      <span className="flex items-baseline gap-2">
                        <span className="font-semibold text-amp-ink dark:text-amp-dark-ink">
                          {formatDollars(c.ty)}
                        </span>
                        <span className={deltaClass(d)}>{formatComp(d)}</span>
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          <TrendChart
            points={data.points}
            grain={data.grain}
            ariaLabel={
              data.grain === 'hour'
                ? 'Hourly sales, this year versus last year'
                : 'Daily sales, this year versus last year'
            }
          />
        </div>
      )}
    </TileShell>
  );
}
